import React, { useEffect, useMemo, useState, useRef } from "react"
import gsap from "gsap"
import ScrollTrigger from "gsap/ScrollTrigger"

import styles from "./Counter.module.scss"

import { COLOURS } from "../../pages/index"

type CounterProps = {
  // Make sure these are the same number.
  countAmount: number
  number: number
  label?: string
  backgroundColour: COLOURS
  textColour: COLOURS.EXTRADARK | COLOURS.OFFWHITE
}

const Counter: React.FC<CounterProps> = ({
  label,
  countAmount,
  number,
  backgroundColour,
  textColour,
}) => {
  // Splits the number into an array of digits
  const startArray = useMemo(
    () =>
      (number - countAmount)
        .toString()
        .split("")
        .map(string => Number(string)),
    [number, countAmount]
  )

  const finishArray = useMemo(
    () =>
      number
        .toString()
        .split("")
        .map(string => Number(string)),
    [number]
  )

  const container = useRef(null)
  const [refs, setRefs] = useState(finishArray.map(() => React.createRef()))
  const [currentNumber, setCurrentNumber] = useState<number[]>(startArray)
  const [countIteration, setCountIteration] = useState(0)

  const Y_DISTANCE = 40

  useEffect(() => {
    ScrollTrigger.create({
      trigger: container.current!,
      start: "center bottom",
      once: true,
      onEnter: () => incrementNumber(),
    })
  }, [])

  useEffect(() => {
    if (currentNumber.join("") === startArray.join("")) return
    showNumbers()
  }, [currentNumber])

  const incrementNumber = () => {
    // Add 1 to the number at the end of the array.
    // If it equals 10, set it to 0 and increment the previous index.
    let newNumberArray = [...currentNumber]
    let arrayIndex = newNumberArray.length - 1
    let shouldContinue = true

    while (shouldContinue) {
      const newNumber = Number(newNumberArray[arrayIndex]) + 1
      if (newNumber === 10) {
        // reduce index and increment the next number to the left.
        newNumberArray.splice(arrayIndex, 1, 0)
        arrayIndex--
      } else {
        newNumberArray.splice(arrayIndex, 1, newNumber)
        shouldContinue = false
      }
    }
    hideChangedNumbers(newNumberArray)
  }

  const hideChangedNumbers = (newNumberArray: number[]) => {
    const duration = Math.max(1 * (countIteration / countAmount), 0.15)
    // Work out the progress through the count and adjust the duration accordingly.

    // We only want to fade out the numbers that are different to the current state.
    const hasNumberChanged: boolean[] = newNumberArray.map(
      (number, i) => number !== currentNumber[i]
    )

    gsap.to(
      refs.map(ref => ref.current),
      {
        y: (i: number) => (hasNumberChanged[i] ? -Y_DISTANCE : 0),
        opacity: (i: number) => (hasNumberChanged[i] ? 0 : 1),
        duration: (i: number) => (hasNumberChanged[i] ? duration : 0),
        onComplete: () => {
          //  Set the Y position for numbers that have changed before updating the number in state.
          let changedElements: any = []
          hasNumberChanged.forEach(
            (isChanged: boolean, i: number) => isChanged && changedElements.push(refs[i].current)
          )
          gsap.set(changedElements, { y: Y_DISTANCE })
          setCurrentNumber(newNumberArray)
          setCountIteration(prev => prev + 1)
        },
      }
    )
  }

  const showNumbers = () => {
    gsap.to(
      refs.map(ref => ref.current),
      {
        y: 0,
        opacity: 1,
        onComplete: () => {
          currentNumber.join("") !== number.toString() && incrementNumber()
        },
      }
    )
  }

  return (
    <div className={styles.container} ref={container}>
      <div className={styles.numbers} style={{ background: backgroundColour }}>
        {refs.map((el, i: number) => (
          <span key={i} ref={refs[i]} style={{ color: textColour }}>
            {currentNumber[i]}
          </span>
        ))}
      </div>
      {label && <h4>{label}</h4>}
    </div>
  )
}

export default Counter
